// CountdownPill — live "⏰ Ends in 4d 12h 32m 15s" badge.
// Used beside plan offer labels. Updates every second. When the offer_ends_at
// timestamp is in the past, the pill switches to "Offer ended" in muted style
// so layout doesn't shift.
import { useEffect, useState } from "react";

function formatRemainder(ms) {
  if (ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function CountdownPill({ endsAt, testId, size = "sm" }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endsAt) return undefined;
    const target = new Date(endsAt).getTime();
    if (Number.isNaN(target)) return undefined;
    // While the offer is ≤24h away, tick every second for urgency. Beyond
    // that, ticking once a minute is enough to save CPU on long-living tabs.
    const intervalMs = target - Date.now() < 24 * 3600 * 1000 ? 1000 : 60000;
    const handle = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [endsAt]);

  if (!endsAt) return null;
  const target = new Date(endsAt).getTime();
  if (Number.isNaN(target)) return null;

  const remaining = target - now;
  const sizeCls = size === "lg" ? "text-xs px-2.5 py-1" : "text-[0.65rem] px-2 py-0.5";

  if (remaining <= 0) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-md bg-slate-100 ${sizeCls} font-semibold text-slate-500`}
        data-testid={testId || "countdown-ended"}
      >
        ⌛ Offer ended
      </span>
    );
  }

  // Pulse red when the offer ends in less than 1 hour for extra urgency.
  const urgent = remaining < 3600 * 1000;
  const cls = urgent
    ? "bg-red-600 text-white animate-pulse"
    : "bg-red-100 text-red-800 ring-1 ring-red-200";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md ${sizeCls} font-bold tabular-nums ${cls}`}
      data-testid={testId || "countdown-active"}
      title={`Offer ends ${new Date(endsAt).toLocaleString()}`}
    >
      ⏰ Ends in {formatRemainder(remaining)}
    </span>
  );
}
